import {
    showSuccessMessage,
    showErrorMessage,
} from "../../models/store/storeActions";
import { PlannedWorkShiftSaveDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { savePlannedWorkShift } from "../../services/plannedWorkShiftService";
import { ApiError } from "../../services/baseService";
import { reloadPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { useTranslation } from "react-i18next";
import { RecurrenceAction } from "../../models/common/enums";
import {
    createRecurringShiftInstance,
    editRecurringShift,
} from "../../services/recurringShiftService";
import { Base } from "../../framework/base";
import { RecurringShiftInstanceType } from "../../models/recurringShift/recurringShift";
import { getEmployees } from "../../services/employeeServiceNew";
import { setGroupEmployees as setGroupEmployeesAction } from "../../store/employeeSlice";

const getSaveForRecurrenceAction = (
    data: PlannedWorkShiftSaveDto,
    recurrenceAction: RecurrenceAction,
    shift: PlannedWorkShiftSaveDto
) => {
    // No recurrence-action specified,
    if (!recurrenceAction) {
        return savePlannedWorkShift(data);
    }

    const recurringId = data?.recurrence?.recurringShiftId;
    if (!recurringId) return;

    if (recurrenceAction === RecurrenceAction.OnlyThis) {
        // Edit this planned shift only if instance already overridden or generated
        if (
            shift.isOverridenFromRecurrence ||
            shift.isGeneratedFromRecurrence
        ) {
            return savePlannedWorkShift(data);
        }

        return createRecurringShiftInstance({
            recurringShiftId: recurringId,
            recurringShiftRowId: data.recurrence.rowId,
            date: Base.dayjsToJsonDate(shift.startTime), // Use original shift startTime instead off updated.
            type: RecurringShiftInstanceType.Override,
            shift: data,
        });
    } else {
        return editRecurringShift(recurringId, {
            rowId: data.recurrence.rowId,
            startDate: data.recurrence.recurrenceStartDate,
            splitFromDate:
                recurrenceAction === RecurrenceAction.AllFuture
                    ? // Use original shift startTime instead off updated.
                    Base.dayjsToJsonDate(shift.startTime)
                    : null,
            startTime: data.startTime,
            endTime: data.endTime,
            label: data.label,
            comment: data.comment,
            intervalInWeeks:
                data.recurrence?.intervalInWeeks &&
                data.recurrence.intervalInWeeks > 0
                    ? data.recurrence.intervalInWeeks
                    : 1,
            repeatDays: data.recurrence?.repeatDays,
            endDate: data.recurrence?.recurrenceEndDate,
            employeeId: data.employeeId ?? undefined,
            vehicleId: data.vehicleId ?? undefined,
            routeId: data.routeId ?? undefined,
        });
    }
};

export const useSavePlannedWorkShiftWithFeedback = (
    shift: PlannedWorkShiftSaveDto,
    recurrenceAction: RecurrenceAction,
    onSuccess?: () => void,
    onFailure?: (error: any) => void
) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const showUnemployable = useAppSelector(
        state => state.workShiftPlanning.filters.showUnemployable
    );
    const selectedEmployeeIds = useAppSelector(
        state => state.workShiftPlanning.filters.selectedEmployeeIds
    );
    const selectedEmployeeGroupId = useAppSelector(
        state => state.workShiftPlanning.filters.selectedEmployeeGroupId
    );
    const filterStartDate = useAppSelector(
        state => state.workShiftPlanning.filters.startDate
    );
    const filterEndDate = useAppSelector(
        state => state.workShiftPlanning.filters.endDate
    );

    const groupEmployees = useAppSelector(state => state.employees?.groupEmployees ?? []);
    const groupEmployeesGroupId = useAppSelector(state => state.employees?.groupEmployeesGroupId ?? null);
    const submit = (data: PlannedWorkShiftSaveDto) => {
        getSaveForRecurrenceAction(data, recurrenceAction, shift)
            .then(() => {
                dispatch(showSuccessMessage(t("common.saveSuccess")));
                dispatch(reloadPlannedWorkShifts());

                // If user has Show Unemployable turned off and the saved shift has no employee
                if (!showUnemployable && !data.employeeId) {
                    dispatch(showSuccessMessage(t("workShiftPlanning.unassignedShiftsNotVisible")));
                }

                // If the saved shift is not visible due to current filters, notify the user.
                // Case 1: An employee was set but the current employee selection doesn't include them.
                const employeeHiddenBySelection =
                    !!data.employeeId &&
                    !!selectedEmployeeIds &&
                    selectedEmployeeIds.length > 0 &&
                    !selectedEmployeeIds.includes(data.employeeId);

                // Case 2: The shift's start date is outside of the currently selected date range (pay period).
                let startOutsideRange = false;
                try {
                    if (data.startTime && filterStartDate && filterEndDate) {
                        const savedDay = Base.dayjsToJsonDate(data.startTime);
                        startOutsideRange = Base.isBefore(savedDay, filterStartDate, "day") || Base.isAfter(savedDay, filterEndDate, "day");
                    }
                } catch (e) {}

                if (employeeHiddenBySelection || startOutsideRange) {
                    dispatch(showSuccessMessage(t("workShiftPlanning.shiftSavedHiddenByFilters")));
                } else if (selectedEmployeeGroupId && data.employeeId) {
                    // If a specific employee group is selected in filters, check whether the saved employee belongs to it.
                    if (groupEmployeesGroupId === selectedEmployeeGroupId && groupEmployees.length > 0) {
                        const found = groupEmployees.some(e => e.id === data.employeeId);
                        if (!found) {
                            dispatch(showSuccessMessage(t("workShiftPlanning.shiftSavedHiddenByFilters")));
                        }
                    } else {
                        getEmployees(selectedEmployeeGroupId)
                            .then((res) => {
                                const fetched = res?.data ?? [];
                                dispatch(setGroupEmployeesAction({ groupId: selectedEmployeeGroupId, employees: fetched }));
                                const found = fetched.some(e => e.id === data.employeeId);
                                if (!found) {
                                    dispatch(showSuccessMessage(t("workShiftPlanning.shiftSavedHiddenByFilters")));
                                }
                            })
                            .catch(() => {});
                    }
                }

                onSuccess?.();
            })
            .catch((error: ApiError) => {
                dispatch(
                    showErrorMessage(
                        error?.status === 409
                            ? t("common.saveConflict")
                            : t("common.saveFailed")
                    )
                );
                onFailure?.(error);
            });
    };

    return submit;
};
